/**
 * Gets the corresponding hex code value for a given color and shade of brand colors.
 */
export function getBrandColorHex(color: keyof BrandColorTokens, shade: keyof BrandColorShades) {
  return newBrandColors[color][shade];
}

export type BrandColorTokens = {
  red: BrandColorShades;
  yellow: BrandColorShades;
  green: BrandColorShades;
  blue: BrandColorShades;
  violet: BrandColorShades;
  gray: BrandColorShades;
};

export type BrandColorShades = {
  50: string;
  150: string;
  300: string;
  500: string;
  700: string;
  900: string;
  950: string;
};

// Brand colors from: https://www.figma.com/design/o0nXlmHB4Zqhu1i0j8BXpP/Hightouch-Design-System?m=dev&node-id=3673%3A4350
const newBrandColors: BrandColorTokens = {
  red: {
    "50": "#FFF4F4",
    "150": "#FEE4E3",
    "300": "#FEB1B5",
    "500": "#F85D3E",
    "700": "#DE2D22",
    "900": "#76230E",
    "950": "#2B0205",
  },
  yellow: {
    "50": "#FFF6E4",
    "150": "#FEE6CA",
    "300": "#F9BA3E",
    "500": "#DB752C",
    "700": "#BA5821",
    "900": "#65320E",
    "950": "#240802",
  },
  green: {
    "50": "#E3FCEE",
    "150": "#B2F9D4",
    "300": "#40DE9E",
    "500": "#2EA281",
    "700": "#228457",
    "900": "#0F492E",
    "950": "#021504",
  },
  blue: {
    "50": "#EBF9FC",
    "150": "#CCF0F9",
    "300": "#8BCDF0",
    "500": "#3498DA",
    "700": "#2D71DC",
    "900": "#153E7E",
    "950": "#02131B",
  },
  violet: {
    "50": "#F9F5FF",
    "150": "#EAE7FE",
    "300": "#C6BFF1",
    "500": "#9280F4",
    "700": "#765BF0",
    "900": "#421BB0",
    "950": "#12043E",
  },
  gray: {
    "50": "#F3F7F5",
    "150": "#E4ECE7",
    "300": "#BDC7C2",
    "500": "#88938D",
    "700": "#6D7772",
    "900": "#3B413E",
    "950": "#101111",
  },
};

const colors = {
  new: {
    ...newBrandColors,
  },
  // Product colors from: https://www.figma.com/design/rLsSTygVap7zagoErmncwm/Styles?m=dev&node-id=1096%3A44996
  product: {
    forest: {
      "50": "#f8fbfc",
      "100": "#effafb",
      "200": "#d3f1f3",
      "300": "#b3d9db",
      "400": "#75b7bb",
      "500": "#488589",
      "600": "#1d656a",
      "700": "#03484d",
      "800": "#043d41",
      "900": "#002528",
    },
    teal: {
      "50": "#f5fcff",
      "100": "#ebfcff",
      "200": "#cef7fd",
      "300": "#a5e0f9",
      "400": "#6ac1e7",
      "500": "#349ac6",
      "600": "#0e6b93",
      "700": "#0A4F6D",
      "800": "#063448",
      "900": "#021822",
    },
    space: {
      "50": "#f5f8ff",
      "100": "#ecf1fe",
      "200": "#c9d8fe",
      "300": "#adbeeb",
      "400": "#8ea3d9",
      "500": "#6C85C5",
      "600": "#425c9d",
      "700": "#2e447d",
      "800": "#222f51",
      "900": "#0c1428",
    },
    warning: {
      "50": "#fffcf5",
      "100": "#fef9ec",
      "200": "#feeec1",
      "300": "#feda76",
      "400": "#f5c24d",
      "500": "#E49A38",
      "600": "#B35808",
      "700": "#9E4D06",
      "800": "#6D3708",
      "900": "#412106",
    },
    danger: {
      "50": "#fffbfa",
      "100": "#ffeceb",
      "200": "#ffe2e0",
      "300": "#f9c8c4",
      "400": "#f69993",
      "500": "#d25046",
      "600": "#A8271E",
      "700": "#7C130C",
      "800": "#410A06",
      "900": "#411008",
    },
    gray: {
      "50": "#fbfcfd",
      "100": "#F2F4F7",
      "200": "#e8edf2",
      "300": "#dbe1e8",
      "400": "#bbc5d0",
      "500": "#9aa6b2",
      "600": "#697786",
      "700": "#4E5C6B",
      "800": "#364452",
      "900": "#252d36",
    },
    peridot: {
      "50": "#FDFFF5",
      "100": "#F8FBEA",
      "200": "#dee3bf",
      "300": "#CED79B",
      "400": "#bfc88e",
      "500": "#a4b05d",
      "600": "#869244",
      "700": "#5B651F",
      "800": "#475013",
      "900": "#262b09",
    },
    lightspeed: {
      "50": "#fdfff5",
      "100": "#f9ffe5",
      "200": "#ecf9bd",
      "300": "#e1f49a",
      "400": "#caf239",
      "500": "#adce37",
      "600": "#8da929",
      "700": "#657c0f",
      "800": "#415300",
      "900": "#2b3700",
    },
    avocado: {
      "50": "#f7fdf8",
      "100": "#effaf2",
      "200": "#D0F1D8",
      "300": "#99dbab",
      "400": "#76b586",
      "500": "#669d74",
      "600": "#3b864d",
      "700": "#266335",
      "800": "#0D491C",
      "900": "#073012",
    },
    grass: {
      "50": "#f5fffa",
      "100": "#ecfef5",
      "200": "#b8f2d4",
      "300": "#9FE8C3",
      "400": "#79d5a6",
      "500": "#4FC26B",
      "600": "#299944",
      "700": "#006a34",
      "800": "#004723",
      "900": "#002C15",
    },
    sky: {
      "50": "#f8fbfc",
      "100": "#ecf3f9",
      "200": "#C5D9E8",
      "300": "#9cc1df",
      "400": "#71A3CC",
      "500": "#5581a5",
      "600": "#386c96",
      "700": "#2a567a",
      "800": "#163C5B",
      "900": "#011c32",
    },
    cyan: {
      "50": "#f5feff",
      "100": "#e5fcff",
      "200": "#C0F4FC",
      "300": "#8cdbe8",
      "400": "#3dbfd9",
      "500": "#1098B7",
      "600": "#197999",
      "700": "#1b5c78",
      "800": "#0f4258",
      "900": "#062837",
    },
    ocean: {
      "50": "#f5f8ff",
      "100": "#ebeffa",
      "200": "#b4c7f9",
      "300": "#90aefc",
      "400": "#658df4",
      "500": "#4b6ec8",
      "600": "#2d52b1",
      "700": "#24418c",
      "800": "#11275f",
      "900": "#071537",
    },
    plum: {
      "50": "#f8f5ff",
      "100": "#eee9fb",
      "200": "#d9cbfb",
      "300": "#BEA8F7",
      "400": "#9774ef",
      "500": "#7958cd",
      "600": "#633CC6",
      "700": "#3E1B95",
      "800": "#301a69",
      "900": "#14053a",
    },
    electric: {
      "50": "#f9f4ff",
      "100": "#f1e7fc",
      "200": "#e5d3f9",
      "300": "#cfb5eb",
      "400": "#BC85FA",
      "500": "#ac61ff",
      "600": "#8743d2",
      "700": "#50188D",
      "800": "#320D59",
      "900": "#1a0730",
    },
  },
  // -----------------------------------
  // Legacy colors
  // -----------------------------------
  gray: {
    "50": "#FBFCFD",
    "100": "#F2F4F7",
    "200": "#E8EDF2",
    "300": "#DBE1E8",
    "400": "#BBC5D0",
    "500": "#9AA6B2",
    "600": "#6B7682",
    "700": "#4E5C6B",
    "800": "#364452",
    "900": "#191A1C",
  },
  blue: {
    "50": "#edf9fe",
    "100": "#d1f1fc",
    "200": "#57d7ff",
    "300": "#007cc1",
  },
  green: {
    "50": "#f6fdf4",
    "100": "#e6f9e0",
    "200": "#c5ea41",
    "300": "#00a341",
  },
  yellow: {
    "100": "#fff2c3",
    "200": "#f4ce41",
    "300": "#e99a00",
  },
  red: {
    "100": "#fed8cf",
    "200": "#f35c20",
    "300": "#af342c",
  },
  purple: {
    "100": "#d7d8ff",
    "200": "#9467f2",
    "300": "#5400be",
  },
};

const themeColors = {
  ...colors,
  brand: {
    text: {
      light: colors.new.gray["950"],
      dark: colors.new.gray["50"],
    },
    textSecondary: {
      light: colors.new.gray["700"],
      dark: colors.new.gray["300"],
    },
    background: {
      light: "white",
      dark: colors.new.gray["950"],
    },
    backgroundSecondary: {
      light: colors.new.gray["50"],
      dark: colors.new.gray["900"],
    },
    border: {
      light: colors.new.gray["300"],
      dark: colors.new.gray["900"],
    },
    header: {
      border: {
        light: "rgba(189, 199, 194, 0.5)",
        dark: "rgba(59, 65, 62, 0.5)",
      },
      backgroundSecondary: {
        light: "rgba(251, 252, 253, 0.50)",
        dark: "rgba(59, 65, 62, 0.30)",
      },
    },
    accent: {
      primary: colors.new.green["300"],
      secondary: colors.new.green["500"],
      compliment: colors.new.gray["950"],
    },
    logo: {
      light: colors.new.gray["950"],
      dark: "#ffffff",
    },
    black: "#000000",
    white: "#ffffff",
    error: colors.new.red["700"],
    success: colors.new.green["500"],
  },
  // -----------------------------------
  // Legacy colors
  // -----------------------------------
  black: colors.gray["900"],
  forest: "#00584f",
  lightspeed: colors.green["200"],
  lightspeedLight: "#D5F378",
  space: "#222F51",
  spaceLight: "#314475",
  ocean: "#336BFB",
  oceanLight: "#5384FF",
  grass: colors.green["300"],
  grassDark: "#00872E",
  grassLight: "#00B849",
  peridot: "#F1F6D4",
  plum: colors.purple["300"],
  plumLight: "#7A55D9",
  electric: colors.purple["200"],
  sky: "#E2EDF6",
  avocado: colors.green["100"],
  teal: colors.blue["300"],
  tealLight: "#028ad4",
  mint: "#40e398",
  mintLight: "#43f09f",
  cyan: colors.blue["200"],
  error: colors.red["300"],
  // @TODO: swap these for new brand colors
  text: {
    primary: colors.new.gray["950"],
    secondary: colors.gray["600"],
    placeholder: colors.gray["500"],
  },
  base: {
    border: colors.gray["300"],
    darkBorder: colors.gray["600"],
    divider: colors.gray["200"],
    background: colors.gray["100"],
    lightBackground: colors.gray["50"],
    darkBackground: colors.gray["900"],
  },
  primary: {
    darkGreen: "#00584f", // Forest
    lightGreen: "#40e398", // Mint
    charcoal: colors.gray["900"],
    white: "#ffffff",
  },
  // Gradients
  oceanGradientDark: "#336BFB",
  oceanGradientLight: "#7C9CEF",
  grassGradientDark: "#00A341",
  grassGradientLight: "rgba(0, 163, 65, 0.5)",
  skyGradientDark: "#D9E9FF",
  skyGradientLight: "rgba(226, 237, 246, 0)",
  tealGradientDark: "#1B5C78",
  tealGradientLight: "#197999",
  quoteAuthorGradient: "linear-gradient(to right, #FFFFFF -70%, #C5D3F6 200%)",
  infographicGradient: "linear-gradient(to bottom, #37D070 -50%, #21936D 10%, #15735D 100%)",
  radialGradient: {
    lightspeed:
      "radial-gradient(50% 50% at 50% 50%, var(--chakra-colors-lightspeed) 0%, rgba(197, 234, 65, 0.00) 100%)",
    mint: "radial-gradient(50% 50% at 50% 50%, var(--chakra-colors-mint) 0%, rgba(64, 227, 152, 0.00) 100%)",
    cyan: "radial-gradient(79.92% 79.92% at 50% 20.08%, var(--chakra-colors-cyan) 0%, rgba(87, 215, 255, 0.00) 100%)",
    avocado: "radial-gradient(50% 50% at 50% 50%, var(--chakra-colors-avocado) 0%, rgba(230, 249, 224, 0.00) 100%)",
    teal: "radial-gradient(50% 50% at 50% 50%, var(--chakra-colors-teal) 0%, rgba(230, 249, 224, 0.00) 100%)",
    purple: "radial-gradient(50% 50% at 50% 50%, var(--chakra-colors-purple-300) 0%, rgba(148, 103, 242, 0.00) 100%)",
  },
  // Customers
  wmg: "#0032A0",
  cars: "#65299D",
  chime: "#1EC677",
  intelycare: "#E5098C",
  ramp: "#787868",
  lucidchart: "#000000",
  gorgias: "#0075FF",
  chalhoub: "#618eb4",
  wellthy: "#E12723",
  mews: "#ff5e64",
  prestashop: "#a4dbe8",
  circleci: "#049b4a",
  retool: "#6875d9",
  vendr: "#5e2df5",
  blend: "#00146b",
  nandos: "#eb3f56",
  zeplin: "#ee6723",
  gosite: "#0058ff",
  seesaw: "#1b70c0",
  "auto-trader": "#222B5E",
  "on-deck": "#315be8",
  "compare-club": "#0ac16a",
  "the-zebra": "#574cfa",
  petsmart: "#0064b1",
};

export default themeColors;

export type Colors = typeof themeColors;
